import { config } from "@/config";
import type { EstimateDetailResponse } from "@/interfaces/estimate-detail";
import type { AddNoteBody, Estimation } from "@/interfaces/estimation.interface";
import type { LineItemElementsResponse, LineItemSearchDataResponse } from "@/interfaces/line-item-elements-response";
import type { UpdateMetadataForFinalEstimatePayload, UpdateMetadataForFinalEstimateResponse } from "@/interfaces/payloads/final_estimate";
import type { CreateOrUpdateItemLinePayload, CreateOrUpdateItemLineResponse } from "@/interfaces/payloads/line-item";
import type {
  ExportToXactimateMissMappingsResponse,
  ExportToXactimateTransactionResponse,
  XactimateRequest,
  XactimateMappingResponse,
  IXactimateUserRowPayload,
  IAddUserXactimateMappingResponse,
  IMissingMapping,
} from "@/interfaces/payloads/xactimate";
import { Provider } from "@/providers/http.provider";
import { objectToQueryString } from "@/util/transform";
import { getInternalOrgOverride } from "@/redux/actions/app.actions";
import type { EstimateType } from "@/enums/estimate-type";
import type { LineItemData } from "@/interfaces/payloads/line-item";

import type {
  PdfFileFormat,
  MissingPricingInfo,
  MissingTMPricingInfo,
  MissingDimensionInfo,
  PricingAndDimensionInfo,
  PaginationParams,
  PaginationFilters,
  PaginatedResult,
  PDFPreviewData,
} from "@rebuild/shared";

interface ValidatePDFExportResponse {
  success: boolean;
  data?: {
    pricing: MissingPricingInfo[] | MissingTMPricingInfo[];
    dimension: MissingDimensionInfo[];
    requiresOfficeSelection?: boolean;
  };
}

interface ValidateXactimateExportResponse {
  success: boolean;
  data: {
    missingFields: string[];
    missingMappings: IMissingMapping[];
  };
}

export interface ValidateUploadedPdfFormatResponse {
  format: Exclude<PdfFileFormat, "OTHER"> | "UNKNOWN";
}

interface PDFPreviewResponse {
  success: boolean;
  data?: PDFPreviewData;
  error?: string;
}

interface ZipTaxRateResponse {
  success: boolean;
  data?: { zipTaxRate?: number };
  error?: string;
}

export interface PasteLineItemResult {
  tupleId: string;
  lineItemId: string;
  pageId: string;
  lineItem: string;
  newTupleId?: string;
  success?: boolean;
  error?: string;
}

export interface PasteLineItemsResponse {
  success: boolean;
  data?: {
    copiedItems: PasteLineItemResult[];
    failedItems: PasteLineItemResult[];
    totalRequested: number;
    totalCopied: number;
    totalFailed: number;
  };
  error?: string;
}

export interface PasteLineItemPayload {
  selectedItems: {
    tupleId: string;
    lineItemId: string;
    pageId: string;
    lineItem: string;
  }[];
  estimateId: string;
  destinationPageId: string;
  cutOperation: boolean;
}

class EstimateProvider extends Provider {
  constructor() {
    super({
      baseURL: `${config.baseAPIUrl}/rest/estimates/`,
    });
  }

  async getEstimates(estimateId?: string): Promise<Estimation[]> {
    const orgOverride = getInternalOrgOverride();
    const params: Record<string, string> = {};
    if (estimateId) params.estimateId = estimateId;
    if (orgOverride) params.overrideOrganizationId = orgOverride.organizationId;
    return super.get<Estimation[]>(`get-estimates-metadata?${objectToQueryString(params)}`);
  }

  async getEstimatesPaginated(params: PaginationParams & PaginationFilters): Promise<PaginatedResult<Estimation> & PaginationParams> {
    const queryParams: Record<string, string | number | boolean> = {
      page: params.page,
      limit: params.limit,
    };
    if (params.status && params.status.length > 0) queryParams.status = params.status.join(",");
    if (params.assigneeId) queryParams.assigneeId = params.assigneeId;
    if (params.search) queryParams.search = params.search;
    const orgOverride = getInternalOrgOverride();
    if (orgOverride) queryParams.overrideOrganizationId = orgOverride.organizationId;
    return super.get<PaginatedResult<Estimation> & PaginationParams>(`get-estimates-metadata?${objectToQueryString(queryParams)}`);
  }

  async getEstimateDetail(estimateId: string): Promise<EstimateDetailResponse> {
    const params: Record<string, string> = { estimateId };
    const orgOverride = getInternalOrgOverride();
    if (orgOverride) params.overrideOrganizationId = orgOverride.organizationId;
    return super.get<EstimateDetailResponse>(`get_final_estimates?${objectToQueryString(params)}`);
  }

  async updateOrCreateLineItem(payload: CreateOrUpdateItemLinePayload): Promise<CreateOrUpdateItemLineResponse> {
    return super.post<CreateOrUpdateItemLineResponse>("updateOrCreateLineItem", payload);
  }

  async updateMetadataFinalEstimate(payload: UpdateMetadataForFinalEstimatePayload) {
    return super.post<UpdateMetadataForFinalEstimateResponse>("updateOrCreateFinalEstimateMetadata", payload);
  }

  async updateEstimateField(payload: { estimaticsData: Partial<Estimation> }): Promise<CreateOrUpdateItemLineResponse> {
    return super.post<CreateOrUpdateItemLineResponse>("updateEstimaticsEstimate", payload);
  }

  async createEstimaticsEstimate(payload: {
    estimaticsData: Estimation;
    scopeNoteUploadEnabled: boolean;
    addressQuery?: string;
  }): Promise<{ success: boolean; estimaticsData: Estimation }> {
    return super.post<{ success: boolean; estimaticsData: Estimation }>("createEstimaticsEstimate", payload);
  }

  async validateDocusketch(payload: { docusketchId: string }): Promise<{ status: number }> {
    return super.post<{ status: number }>("validateDocusketch", payload);
  }

  async validateUploadedPdfFormat(payload: { remotePath: string }): Promise<ValidateUploadedPdfFormatResponse> {
    return super.post<ValidateUploadedPdfFormatResponse>("validateUploadedPdfFormat", payload);
  }

  async mergePages(payload: { estimateId: string; sourcePageId: string; destinationPageId: string }): Promise<{ success: boolean }> {
    return super.post<{ success: boolean }>("mergePages", payload);
  }

  async updateMeasurement(payload: { estimaticsData: Partial<Estimation> }): Promise<{
    success: boolean;
  }> {
    return super.post<{
      success: boolean;
    }>("updateEstimaticsEstimate", payload);
  }

  async searchLineItems(
    search: string,
    estimateType: EstimateType,
    searchMode?: string,
    estimateId?: string,
  ): Promise<LineItemSearchDataResponse> {
    const query: Record<string, string | boolean> = {
      searchString: search,
      returnIds: true,
      estimateType,
    };
    if (searchMode && searchMode !== "all") {
      query.searchMode = searchMode;
    }
    if (estimateId) {
      query.estimateId = estimateId;
    }

    return super.get<LineItemSearchDataResponse>(`/v2/searchLineItems?${objectToQueryString(query)}`);
  }
  async getLineItemElements(lineItemId: string): Promise<LineItemElementsResponse> {
    const query = {
      lineItemId,
    };

    return super.get<LineItemElementsResponse>(`getLineItemElements?${objectToQueryString(query)}`);
  }

  async addUserXactimateMapping(payload: { mapping: IXactimateUserRowPayload }): Promise<IAddUserXactimateMappingResponse> {
    return super.post<IAddUserXactimateMappingResponse>("addUserXactimateMapping", payload);
  }

  async applyTPARules(payload: {
    estimateId: string;
    tpaId: string;
    carrierId: string;
  }): Promise<{ success: boolean; queued: boolean; jobId?: string }> {
    return super.post<{ success: boolean; queued: boolean; jobId?: string }>("applyTPARules", payload);
  }

  async restoreDocument(payload: { estimateId: string }): Promise<{ success: boolean }> {
    return super.post<{ success: boolean }>("restoreDocument", payload);
  }

  // async getEstimateMetadata(estimatePath: string): Promise<{ success: boolean; data: any[] }> {
  //   const query = {
  //     requestType: 'devMetadata',
  //     estimatePath
  //   };

  //   return super.get<SearchLineItemResponse>(`get_estimate?${objectToQueryString(query)}`);
  // }

  async exportToXactimate(
    query: XactimateRequest,
    body?: { fixedMappingBody: string },
  ): Promise<ExportToXactimateMissMappingsResponse | ExportToXactimateTransactionResponse> {
    return super.post<ExportToXactimateMissMappingsResponse | ExportToXactimateTransactionResponse>(
      `exportToXactimate?${objectToQueryString(query as unknown as Record<string, string | number | boolean>)}`,
      body,
    );
  }

  // Stage 2 of the Xactimate export: uploads photos to the transaction created by exportToXactimate.
  // Uses the v1 route so it authenticates with the same (lasso) token as the rest of the webapp — the
  // v2 route requires a Firebase ID token, which the webapp's shared client does not carry.
  async startXactimatePhotoUpload(query: { estimateId: string; transactionId: string }): Promise<{ success: boolean; status?: string }> {
    return super.post<{ success: boolean; status?: string }>(
      `startXactimatePhotoUpload?${objectToQueryString(query as unknown as Record<string, string | number | boolean>)}`,
    );
  }

  async exportTo1Pager(
    estimateId: string,
    pdfPreviewData: PDFPreviewData,
    filename?: string,
    sendToJobNimbus?: boolean,
  ): Promise<ArrayBuffer> {
    return super.post<ArrayBuffer>(
      `exportTo1Pager?estimateId=${estimateId}`,
      { pdfPreviewData, filename, sendToJobNimbus },
      { responseType: "arraybuffer" },
    );
  }

  async validatePDFExport(estimateId: string): Promise<ValidatePDFExportResponse> {
    return super.get<ValidatePDFExportResponse>(`validatePDFExport?estimateId=${estimateId}`);
  }

  async validateXactimateExport(estimateId: string): Promise<ValidateXactimateExportResponse> {
    return super.get<ValidateXactimateExportResponse>(`validateXactimateExport?estimateId=${estimateId}`);
  }

  async getPDFPreviewData(
    estimateId: string,
    pricingAndDimensionInfo: PricingAndDimensionInfo,
    verbose: boolean = false,
    clearCache: boolean = false,
  ): Promise<PDFPreviewResponse> {
    const body = {
      estimateId,
      pricingAndDimensionInfo,
      verbose,
      clearCache,
    };
    return super.post<PDFPreviewResponse>(`pdfPreviewData`, body);
  }

  /** Look up the current ZipTax sales-tax rate for an address (the estimate's current address). */
  async getZipTaxRate(address: string): Promise<ZipTaxRateResponse> {
    return super.post<ZipTaxRateResponse>(`zipTaxRate`, { address });
  }

  async downloadScopeNotes(estimateId: string): Promise<ArrayBuffer> {
    // Send the viewer's timezone so the PDF renders note timestamps in the same
    // zone the user sees in the app (the server would otherwise default to UTC).
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return super.post<ArrayBuffer>(
      `${estimateId}/downloadScopeNotes`,
      { timeZone },
      {
        responseType: "arraybuffer",
      },
    );
  }

  async getXactimateMapping(estimateId: string): Promise<XactimateMappingResponse> {
    const params = { estimateId };
    return super.get<XactimateMappingResponse>(`/getXactimateMapping?${objectToQueryString(params)}`);
  }

  async getXactimateMappingForLineItem(lineItem: string, lineItemId: string): Promise<XactimateMappingResponse> {
    const params = { lineItem, lineItemId };
    return super.get<XactimateMappingResponse>(`/getXactimateMappingForLineItem?${objectToQueryString(params)}`);
  }

  async batchUpdateOrCreateLineItems(payload: {
    operations: Array<{
      lineItemId: string;
      estimateId: string;
      pageId: string;
      lineItemData: LineItemData;
      source?: string;
      metadata?: Record<string, unknown>;
    }>;
  }): Promise<{
    success: boolean;
    totalOperations: number;
    successfulOperations: number;
    failedOperations: number;
    results: Array<{
      index: number;
      success: boolean;
      data?: LineItemData;
      error?: string;
      lineItemId: string;
    }>;
  }> {
    return super.post("batchUpdateOrCreateLineItems", payload);
  }

  async addScopeNoteToExistingEstimate(estimateId: string, payload: { scopeNoteObject: AddNoteBody }) {
    return super.post(`${estimateId}/documentation`, payload);
  }

  async transcriptToScopeNotes(transcript: string, estimateType: EstimateType): Promise<{ scopeNotes: string }> {
    return super.post<{ scopeNotes: string }>("transcriptToScopeNotes", {
      transcript,
      estimateType,
    });
  }

  async pasteLineItems(payload: PasteLineItemPayload): Promise<PasteLineItemsResponse> {
    return super.post<PasteLineItemsResponse>("pasteLineItems", payload);
  }
}

export default new EstimateProvider();
